import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDataAPI, putDataAPI, deleteDataAPI } from "../../utils/fetchData";
import LayoutHeader from "../../layout/LayoutHeader";
import { updateRemindersForBooking, deleteRemindersForBooking } from "../../utils/remindersData";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";
import { canAccessFinancials } from "../../utils/permissions";
import CustomCalendar from "../../components/Calendar/CustomCalendar";

const EditBooking = () => {
    const { bookingId } = useParams();
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const { modalConfig, showModal, closeModal } = useModal();
    const hasFinancialAccess = canAccessFinancials('bookings');
    const [cityOptions, setCityOptions] = useState([]);
    const [showCityDropdown, setShowCityDropdown] = useState(false);

    // Global City/State/Province auto-fill data (same as ManualAdd.jsx)
    const cityStateData = {
        // United States
        "New York": "NY", "Los Angeles": "CA", "Chicago": "IL", "Houston": "TX", "Phoenix": "AZ",
        "Philadelphia": "PA", "San Antonio": "TX", "San Diego": "CA", "Dallas": "TX", "San Jose": "CA",
        "Austin": "TX", "Jacksonville": "FL", "Fort Worth": "TX", "Columbus": "OH", "Charlotte": "NC",
        "San Francisco": "CA", "Indianapolis": "IN", "Seattle": "WA", "Denver": "CO", "Washington": "DC",
        "Boston": "MA", "El Paso": "TX", "Nashville": "TN", "Detroit": "MI", "Oklahoma City": "OK",
        "Portland": "OR", "Las Vegas": "NV", "Memphis": "TN", "Louisville": "KY", "Baltimore": "MD",
        "Milwaukee": "WI", "Albuquerque": "NM", "Tucson": "AZ", "Fresno": "CA", "Sacramento": "CA",
        "Mesa": "AZ", "Kansas City": "MO", "Atlanta": "GA", "Long Beach": "CA", "Colorado Springs": "CO",
        "Raleigh": "NC", "Miami": "FL", "Virginia Beach": "VA", "Omaha": "NE", "Oakland": "CA",
        "Minneapolis": "MN", "Tulsa": "OK", "Arlington": "TX", "Tampa": "FL", "New Orleans": "LA",
        
        // Canada
        "Toronto": "ON", "Montreal": "QC", "Vancouver": "BC", "Calgary": "AB", "Edmonton": "AB",
        "Ottawa": "ON", "Winnipeg": "MB", "Quebec City": "QC", "Hamilton": "ON", "Kitchener": "ON",
        "London (ON)": "ON", "Victoria": "BC", "Halifax": "NS", "Oshawa": "ON", "Windsor": "ON",
        
        // United Kingdom  
        "London": "England", "Birmingham": "England", "Manchester": "England", "Glasgow": "Scotland",
        "Liverpool": "England", "Edinburgh": "Scotland", "Leeds": "England", "Sheffield": "England",
        "Bristol": "England", "Cardiff": "Wales", "Belfast": "Northern Ireland", "Newcastle (UK)": "England",
        "Nottingham": "England", "Portsmouth": "England", "Plymouth": "England",
        
        // Australia
        "Sydney": "NSW", "Melbourne": "VIC", "Brisbane": "QLD", "Perth": "WA", "Adelaide": "SA",
        "Gold Coast": "QLD", "Newcastle (AU)": "NSW", "Canberra": "ACT", "Sunshine Coast": "QLD",
        "Wollongong": "NSW", "Hobart": "TAS", "Geelong": "VIC", "Townsville": "QLD",
        
        // Germany
        "Berlin": "Berlin", "Hamburg": "Hamburg", "Munich": "Bavaria", "Cologne": "North Rhine-Westphalia",
        "Frankfurt": "Hesse", "Stuttgart": "Baden-Württemberg", "Düsseldorf": "North Rhine-Westphalia",
        "Dortmund": "North Rhine-Westphalia", "Essen": "North Rhine-Westphalia", "Leipzig": "Saxony",
        "Bremen": "Bremen", "Dresden": "Saxony", "Hanover": "Lower Saxony"
        // ... (truncated for brevity, but includes all the global cities)
    };

    const handleCityChange = (e) => {
        const value = e.target.value;
        setEditData(prev => ({ ...prev, city: value }));
        
        if (value.length >= 2) {
            const matches = Object.keys(cityStateData).filter(city => 
                city.toLowerCase().includes(value.toLowerCase())
            );
            setCityOptions(matches);
            setShowCityDropdown(matches.length > 0);
        } else {
            setCityOptions([]);
            setShowCityDropdown(false);
        }
    };

    const selectCity = (city) => {
        const state = cityStateData[city];
        setEditData(prev => ({ 
            ...prev, 
            city: city,
            state: state 
        }));
        setShowCityDropdown(false);
        setCityOptions([]);
    };

    const handleNumberInput = (key, value, prefix = '') => {
        // Remove non-numeric characters except decimal point
        const numericValue = value.replace(/[^0-9.]/g, '');
        setEditData(prev => ({ ...prev, [key]: prefix + numericValue }));
    };

    useEffect(() => {
        loadBookingData();
    }, [bookingId]);

    const loadBookingData = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const response = await getDataAPI('user/getBookings', token);
            
            if (response.data.status === 200 && response.data.data) {
                const bookingsData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
                const booking = bookingsData.find(b => b._id === bookingId);
                
                if (booking) {
                    // Handle legacy city_state field by splitting into separate fields
                    let processedBooking = { ...booking };
                    
                    if (booking.city_state && !booking.city && !booking.state) {
                        const cityStateValue = String(booking.city_state).trim();
                        
                        if (cityStateValue.includes(',')) {
                            const parts = cityStateValue.split(',');
                            const city = parts[0]?.trim() || '';
                            const state = parts[1]?.trim() || '';
                            
                            processedBooking.city = city;
                            processedBooking.state = state;
                        } else if (cityStateValue.includes(' - ')) {
                            const parts = cityStateValue.split(' - ');
                            const city = parts[0]?.trim() || '';
                            const state = parts[1]?.trim() || '';
                            
                            processedBooking.city = city;
                            processedBooking.state = state;
                        } else {
                            // If no delimiter, treat as city and try to auto-fill state
                            processedBooking.city = cityStateValue;
                            if (cityStateData[cityStateValue]) {
                                processedBooking.state = cityStateData[cityStateValue];
                            }
                        }
                        
                        // Remove the old city_state field
                        delete processedBooking.city_state;
                    }
                    
                    setEditData(processedBooking);
                } else {
                    showModal('Not Found', 'Booking not found', 'error');
                    navigate('/bookings/view-all');
                }
            }
        } catch (error) {
            console.error('Error loading booking:', error);
            showModal('Load Error', 'Error loading booking data', 'error');
        } finally {
        }
    };

    const saveData = async () => {
        // Validate required fields
        const requiredFields = [
            { key: 'perf_date', label: 'Performance Date' },
            { key: 'artist', label: 'Artist' },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State/Province' },
            { key: 'venue', label: 'Venue' }
        ];

        const missingFields = requiredFields.filter(field => {
            const value = editData[field.key];
            return !value || (typeof value === 'string' && value.trim() === '');
        });
        
        if (missingFields.length > 0) {
            const fieldNames = missingFields.map(f => f.label).join(', ');
            showModal('Required Fields Missing', `Please fill in the following required fields: ${fieldNames}`, 'error');
            return;
        }

        setIsLoading(true);
        try {
            let response = await putDataAPI('user/updateBooking', {
                bookingId: bookingId,
                data: editData
            }, token);
            console.log(`response::`, response.data);
            
            // Update booking reminders with new data
            try {
                console.log('🔄 Updating booking reminders for:', bookingId);
                const updateResult = updateRemindersForBooking(bookingId, {
                    ...editData,
                    _id: bookingId
                });
                console.log('✅ Booking reminders updated:', updateResult);
            } catch (reminderError) {
                console.error('Error updating booking reminders:', reminderError);
                // Don't block the user flow if reminders fail
            }
            
            setTimeout(() => {
                navigate('/bookings/view-all');
            }, 1500);
        } catch (error) {
            console.error('Error updating booking:', error);
            
            // Check if it's a validation error from backend
            if (error.response?.data?.message?.includes('Required fields missing')) {
                showModal('Validation Error', error.response.data.message, 'error');
            } else {
                showModal('Update Error', 'Error updating booking. Please try again.', 'error');
            }
        }
        setIsLoading(false);
    }

    const deleteBooking = async () => {
        setIsDeleting(true);
        try {
            let response = await deleteDataAPI(`user/deleteBooking/${bookingId}`, token);
            console.log(`delete response::`, response.data);
            
            // Delete associated booking reminders
            try {
                console.log('🗑️ Deleting booking reminders for:', bookingId);
                const deletedCount = deleteRemindersForBooking(bookingId);
                console.log(`✅ Deleted ${deletedCount} booking reminders`);
            } catch (reminderError) {
                console.error('Error deleting booking reminders:', reminderError);
                // Don't block the user flow if reminders fail
            }
            
            showModal('Success', 'Tour date deleted successfully!', 'success');
            setTimeout(() => {
                navigate('/bookings/view-all');
            }, 1500);
        } catch (error) {
            console.error('Error deleting booking:', error);
            showModal('Delete Error', 'Error deleting tour date. Please try again.', 'error');
        }
        setIsDeleting(false);
        setShowDeleteConfirm(false);
    }

    const basicFields = [
        // Required fields first
        { key: 'artist', label: 'Artist', placeholder: 'Enter artist name', required: true },
        { key: 'perf_date', label: 'Performance Date', placeholder: 'Enter date', type: 'date', required: true },
        { key: 'city', label: 'City', placeholder: 'Enter city name', required: true },
        { key: 'state', label: 'State/Province', placeholder: 'Auto-filled', required: true },
        { key: 'venue', label: 'Venue', placeholder: 'Enter venue name', required: true },
        // Optional fields
        { key: 'status', label: 'Status', placeholder: 'Select status', type: 'select', options: ['pending', 'confirmed', 'announced', 'private', 'canceled'] },
        { key: 'show_time', label: 'Set Time', placeholder: 'Enter set time (e.g., 9:00 PM)', type: 'text', required: false },
        { key: 'announce_date', label: 'Announce Date', placeholder: 'Enter announce date', type: 'date' },
        { key: 'on_sale_date', label: 'On Sale Date', placeholder: 'Enter on sale date', type: 'date' },
        { key: 'event_title', label: 'Event Title', placeholder: 'Enter event title' },
        { key: 'guarantee', label: 'Guarantee', placeholder: 'Enter amount', type: 'currency' },
        { key: 'walkout_potential', label: 'Walkout', placeholder: 'Enter amount', type: 'currency' },
        { key: 'sellable_cap', label: 'Sellable Cap', placeholder: 'Enter capacity', type: 'number', max: 1000000 },
        { key: 'promoter', label: 'Promoter', placeholder: 'Enter promoter' },
        { key: 'tix_sold_percent', label: '% Tix Sold', placeholder: 'Enter percentage sold', type: 'number' },
        { key: 'tix_sold', label: 'Tix Sold', placeholder: 'Enter tickets sold', type: 'number' },
        { key: 'current_ticket_count', label: 'Current Ticket Count', placeholder: 'Enter current tickets sold', type: 'number' },
    ];

    const advancedFields = [
        { key: 'appearing_with', label: 'Lineup', placeholder: 'Enter lineup information' },
        { key: 'red_flag', label: 'Red Flag', type: 'checkbox' },
        { key: 'red_flag_notes', label: 'Red Flag Notes', placeholder: 'Enter red flag details', type: 'textarea' },
        { key: 'promoter_company', label: 'Promoter Company', placeholder: 'Enter promoter company' },
        { key: 'promoter_contact_names', label: 'Promoter Contact Name(s)', placeholder: 'Enter contact names' },
        { key: 'promoter_contact_emails', label: 'Promoter Contact Email(s)', placeholder: 'Enter contact emails' },
        { key: 'venue_category', label: 'Venue Category', placeholder: 'Enter venue category' },
        { key: 'billing_type', label: 'Billing Type', placeholder: 'Enter billing type' },
        { key: 'territory', label: 'Territory', placeholder: 'Enter territory' },
        { key: 'advanced_info', label: 'Advanced Info', placeholder: 'Enter advanced information', type: 'textarea' },
            { key: 'deal_type', label: 'Deal Type', placeholder: 'Enter deal type (flat, bonus, etc.)' },
        { key: 'deal_info', label: 'Deal Info', placeholder: 'Enter deal information' },
            { key: 'gross_potential', label: 'Gross Potential', placeholder: 'Enter gross potential' },
            { key: 'net_potential', label: 'Net Potential', placeholder: 'Enter net potential' },
            { key: 'artist_net', label: 'Artist Net', placeholder: 'Enter artist net' },
            { key: 'artist_profit', label: 'Artist Profit', placeholder: 'Enter artist profit' },
            { key: 'split_point', label: 'Split Point', placeholder: 'Enter split point' },
            { key: 'amount_to_split', label: 'Amount to Split', placeholder: 'Enter amount to split' },
        { key: 'ticket_notes', label: 'Ticket Notes', placeholder: 'Enter ticket notes', type: 'textarea' },
        { key: 'ticket_prices_tiers', label: 'Ticket Prices (tiers)', placeholder: 'Enter ticket price tiers' },
            { key: 'ticket_taxes_fees', label: 'Ticket Taxes / Fees', placeholder: 'Enter taxes and fees' },
            { key: 'total_fees', label: 'Total Fees', placeholder: 'Enter total fees' },
            { key: 'deposit_due', label: 'Deposit Due', placeholder: 'Enter deposit due' },
            { key: 'deposit_received', label: 'Deposit Received', placeholder: 'Enter deposit received' },
        { key: 'contract_status', label: 'Contract Status', placeholder: 'Enter contract status' },
        { key: 'currency_conversion', label: 'Currency / Conversion', placeholder: 'Enter currency info' },
        { key: 'additional_clauses', label: 'Additional Clauses', placeholder: 'Enter additional clauses', type: 'textarea' },
        { key: 'schedule', label: 'Schedule', placeholder: 'Enter schedule details' },
        { key: 'notes', label: 'Notes', placeholder: 'Enter additional notes', type: 'textarea' }
    ];

    const renderField = (field) => {
        const value = editData[field.key] || '';

        // Special handling for city field with dropdown
        if (field.key === 'city') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all relative">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={handleCityChange}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                        placeholder={field.placeholder}
                    />
                    
                    {/* City Dropdown */}
                    {showCityDropdown && cityOptions.length > 0 && (
                        <div className="absolute z-10 top-full left-0 right-0 mt-2 overflow-hidden rounded-lg shadow-lg">
                            <div className="max-h-[150px] overflow-y-auto bg-[#1A1A1A] border border-[rgba(255,255,255,0.1)]">
                                {cityOptions.map((city, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 hover:bg-[rgba(255,255,0,0.1)] cursor-pointer transition-colors border-b border-[rgba(255,255,255,0.05)] last:border-b-0"
                                        onClick={() => selectCity(city)}
                                    >
                                        <div className="text-white text-[12px]">{city}</div>
                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">{cityStateData[city]}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Special handling for currency fields
        if (field.type === 'currency') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleNumberInput(field.key, e.target.value, '$')}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                        placeholder={field.placeholder}
                    />
                </div>
            );
        }

        // Special handling for number fields
        if (field.type === 'number') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleNumberInput(field.key, e.target.value)}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                        placeholder={field.placeholder}
                        max={field.max}
                    />
                </div>
            );
        }

        // Select dropdown fields
        if (field.type === 'select') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <select
                        value={value}
                        onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] appearance-none cursor-pointer"
                        style={{ backgroundImage: 'none' }}
                    >
                        <option value="">Select {field.label.toLowerCase()}</option>
                        {field.options.map((option, index) => (
                            <option key={index} value={option}>
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        // Checkbox field
        if (field.type === 'checkbox') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-[14px] h-[14px] overflow-hidden">
                            <input
                                type="checkbox"
                                checked={value || false}
                                onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.checked }))}
                                className="accent-[#FFFF00] cursor-pointer"
                                style={{ width: '14px', height: '14px', minWidth: '14px', minHeight: '14px', maxWidth: '14px', maxHeight: '14px' }}
                            />
                        </div>
                        <label className="text-white text-[12px] cursor-pointer" style={{ fontWeight: 300 }} onClick={() => setEditData(prev => ({ ...prev, [field.key]: !prev[field.key] }))}>
                            {field.label}
                        </label>
                        {value && (
                            <span className="text-[#FFFF00] text-[12px]">✓ Flagged</span>
                        )}
                    </div>
                </div>
            );
        }

        // Textarea fields
        if (field.type === 'textarea') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <textarea
                        value={value}
                        onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                        placeholder={field.placeholder}
                    />
                </div>
            );
        }

        // Date field with CustomCalendar
        if (field.type === 'date') {
            return (
                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <CustomCalendar
                        label={field.label + (field.required ? ' *' : '')}
                        value={value}
                        onChange={(date) => setEditData(prev => ({ ...prev, [field.key]: date }))}
                        placeholder={field.placeholder}
                        showTime={false}
                        required={false}
                        theme="bookings"
                    />
                </div>
            );
        }

        // Regular input fields
        return (
            <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                    {field.label}
                    {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                </label>
                <input
                    type={field.type || 'text'}
                    value={value}
                    onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                    placeholder={field.placeholder}
                    readOnly={field.readOnly}
                />
            </div>
        );
    };


    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Edit Booking</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={saveData}
                            >
                                Save
                            </span>
                            
                            <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => {
                              const canGoBack = window.history.length > 1;
                              if (canGoBack) {
                                navigate(-1);
                              } else {
                                navigate('/');
                              }
                            }}>Go back</span>
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Edit booking details</span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Update the information for this booking entry. Fields marked with <span className="text-[#FFFF00]">*</span> are required.</p>
                            </div>

                            {/* Basic Fields */}
                            <div className="space-y-4 mb-6">
                                {basicFields.map(renderField)}
                            </div>

                            {/* Advanced Section Toggle */}
                            <div className="mb-4">
                                <button
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all flex items-center justify-between"
                                >
                                    <span className="text-white text-[12px]" style={{ fontWeight: 300 }}>Advanced Fields</span>
                                    <svg 
                                        className={`w-4 h-4 text-white transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Advanced Fields */}
                            {showAdvanced && (
                                <div className="space-y-4 mb-6">
                                    <div className="text-[10px] text-[rgba(255,255,255,0.7)] mb-4">
                                        Advanced booking details for comprehensive tracking and AI integration
                                    </div>
                                    {advancedFields.map(renderField)}
                                </div>
                            )}

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                                <button
                                    onClick={saveData}
                                    disabled={isLoading}
                                    className="w-full bg-[#FFFF00] text-black text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,255,0,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Booking'
                                    )}
                                </button>
                            </div>

                            {/* Delete Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF2300] hover:bg-[rgba(255,35,0,0.05)] transition-all mt-4">
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isLoading || isDeleting}
                                    className="w-full bg-[#FF2300] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,35,0,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    Delete Tour Date
                                </button>
                            </div>

                            {/* Delete Confirmation Dialog */}
                            {showDeleteConfirm && (
                                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
                                    <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-6 relative">
                                            <h3 className="text-white text-[18px] font-bold text-left">Delete Tour Date</h3>
                                            <button
                                                onClick={() => setShowDeleteConfirm(false)}
                                                className="text-gray-400 hover:text-white transition-colors"
                                                disabled={isDeleting}
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {/* Content */}
                                        <div className="mb-8">
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] text-left">
                                                Are you sure you want to delete this tour date? This action cannot be undone.
                                            </p>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex space-x-4">
                                            <button
                                                onClick={deleteBooking}
                                                disabled={isDeleting}
                                                className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                            >
                                                {isDeleting ? (
                                                    <>
                                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    'Delete'
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setShowDeleteConfirm(false)}
                                                disabled={isDeleting}
                                                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Help Text */}
                        <div className="mt-6 mb-4">
                            <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                <p>Update any fields as needed.</p>
                                <p>Advanced fields help provide comprehensive data for AI assistance.</p>
                                <p>Changes will be saved and available to your AI assistant.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default EditBooking; 